import CanadianEftBankForm from "components/PayoutManagement/forms/CanadianEftBankForm";
import CanadianEftPersonalForm from "components/PayoutManagement/forms/CanadianEftPersonalForm";
import { PayoutModalPropsType } from "components/PayoutManagement/types";
import { addPayoutMethod, updatePayoutMethod } from "constants/apiUrls";
import { PayoutMethodEnum } from "enum/common";
import { useFormik } from "formik";
import { useAppSelector } from "hooks/useRedux";
import { http } from "middleware/apiMiddleware";
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";
import { useState } from "react";
import toast from "react-hot-toast";
import { RootState } from "store";
import * as Yup from "yup";

interface ICanadianEFTProps {
  data: PayoutModalPropsType;
  afterSubmit: () => void;
  isBankForm: boolean;
  setIsBankForm: (value: boolean) => void;
  setIsSubmitting: (value: boolean) => void;
}

const CanadianEFT: React.FC<ICanadianEFTProps> = ({
  data,
  afterSubmit,
  isBankForm,
  setIsBankForm,
  setIsSubmitting,
}) => {
  const [formDetails, setFormDetails] = useState<any>(null);
  const validationSchemaPersonal = Yup.object().shape({
    firstName: Yup.string()
      .max(256, "Name can be max of 256 characters")
      .required("Required"),
    lastName: Yup.string()
      .max(256, "Name can be max of 256 characters")
      .required("Required"),
    address1: Yup.string()
      .max(1024, "Street Address can be of max 1024 characters")
      .required("Required"),
    city: Yup.object({
      name: Yup.string().required("Required"),
      value: Yup.string().required("Required"),
    }).required("Required"),
    state: Yup.object({
      name: Yup.string().required("Required"),
      value: Yup.string().required("Required"),
    }).required("Required"),
    zipCode: Yup.string()
      .required("Required")
      .test("zipCode", "Invalid zipCode", (value, ctx) => {
        const countryShortCode = ctx.parent.country.value;
        if (postcodeValidatorExistsForCountry(countryShortCode)) {
          return postcodeValidator(value, countryShortCode);
        }
        return true;
      }),
  });
  const validationSchemaBank = Yup.object().shape({
    accountName: Yup.string()
      .max(256, "Account name can be max of 256 characters")
      .required("Required"),
    accountNumber: Yup.string()
      .max(12, "Invalid account number")
      .min(7, "Invalid account number")
      .required("Required"),
    transitNumber: Yup.string()
      .max(5, "Invalid transit number")
      .min(5, "Invalid transit number")
      .required("Required"),
    financialInstitutionNumber: Yup.string()
      .max(3, "Invalid financial instituion number")
      .min(3, "Invalid financial instituion number")
      .required("Required"),
  });

  const [loading, setLoading] = useState(false);

  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );

  const isEdit = data?.type === "edit";
  const initialValuesPersonal = {
    firstName: data?.data?.firstName || "",
    lastName: data?.data?.lastName || "",
    address1: data?.data?.address1 || "",
    city: {
      name: data?.data?.city || selectedBusiness?.address?.city || "",
      value: data?.data?.city || selectedBusiness?.address?.city || "",
    },
    state: {
      name: data?.data?.state || selectedBusiness?.address?.state || "",
      value:
        data?.data?.stateShortCode ||
        selectedBusiness?.address?.stateShortCode ||
        "",
    },
    country: {
      name: data?.data?.country || "Canada",
      value: data?.data?.countryShortcode || "CA",
    },
    zipCode: data?.data?.zipCode || "",
  };

  const initialValuesBank = {
    financialInstitutionNumber: data?.data?.financialInstitutionNumber || "",
    transitNumber: data?.data?.transitNumber || "",
    accountNumber: data?.data?.accountNumber || "",
    accountName: data?.data?.accountName || "",
  };

  const formikRefPersonal = useFormik({
    initialValues: initialValuesPersonal,
    validationSchema: validationSchemaPersonal,
    onSubmit: async (values: any) => {
      try {
        setIsSubmitting(true);
        setFormDetails({ ...values, ...formDetails });
        setIsBankForm(true);
      } catch (error) {
        toast("Unexpected error occurred while adding/updating payout method");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const formikRefBank = useFormik({
    initialValues: initialValuesBank,
    validationSchema: validationSchemaBank,
    onSubmit: async (values: any) => {
      try {
        setIsSubmitting(true);
        const payload = {
          ...formDetails,
          country: countryOptions[0].name,
          countryShortCode: countryOptions[0].value,
          state: formDetails?.state?.name,
          stateShortCode: formDetails?.state?.value,
          city: formDetails?.city?.value,
          ...values,
        };

        if (isEdit) {
          await http.patch(
            updatePayoutMethod(
              PayoutMethodEnum.CANADAEFT_PREVIEW,
              data?.data?.uuid || "",
              selectedBusiness?.uuid || ""
            ),
            payload
          );
        } else {
          await http.post(
            addPayoutMethod(
              PayoutMethodEnum.CANADAEFT_PREVIEW,
              selectedBusiness?.uuid || ""
            ),
            payload
          );
        }
        afterSubmit();
      } catch (error) {
        toast("Unexpected error occurred while adding/updating payout method");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const countryOptions = [
    {
      name: "Canada",
      value: "CA",
    },
  ];

  return (
    <div>
      {!isBankForm ? (
        <CanadianEftPersonalForm formikRef={formikRefPersonal} />
      ) : (
        <CanadianEftBankForm formikRef={formikRefBank} />
      )}
    </div>
  );
};

export default CanadianEFT;
